import React, { useEffect, useState } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  Outlet,
} from "react-router-dom";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import { AuthProvider, useAuth } from "./auth/AuthContext";
import { ViewAsProvider } from "./auth/ViewAsContext";
import AppShell from "./components/layout/AppShell";

// Pages
import LoginPage from "./pages/login";
import Onboarding from "./pages/onboarding";
import AdminDashboard from "./pages/dashboard/admin";
import MentorDashboard from "./pages/dashboard/mentor";
import StudentDashboard from "./pages/dashboard/student";
import ClassesPage from "./pages/office/classes";
import ClassDetailPage from "./pages/office/classes/[classId]";
import StaffPage from "./pages/staff";
import ComposePage from "./pages/compose";
import StepPage from "./pages/step";
import Step1Page from "./pages/step1";
import Step2Page from "./pages/step2";
import Step3Page from "./pages/step3";
import Step4Page from "./pages/step4";
import Step5Page from "./pages/step5";
import Step6Page from "./pages/step6";
import ScienceFairsAdminPage from "./pages/admin/science-fairs";
import FairDatesPage from "./pages/fair-dates";
import AiResearchLabPage from "./pages/learn/ai-research-lab";
import ExampleBoardsPage from "./pages/learn/boards";
import TokenUsagePage from "./pages/admin/token-usage";
import ProfilePage from "./pages/profile";
import MyClassPage from "./pages/my-class";
import StudentMessagesPage from "./pages/messages";
import RequirementsPage from "./pages/requirements";
import ProgressGridPage from "./pages/progress";
import SchedulePage from "./pages/schedule";
import MentorReviewPage from "./pages/review/[projectId]";
import ParentSignInPage from "./pages/parent-signin";
import TrialAccessPage from "./pages/trial-access";
import ApplicationChoicePage from "./pages/application-choice";
import ViewAsSummaryPage from "./pages/view-as-summary";
import ViewAsMessagesPage from "./pages/view-as-messages";
import BookMeetingPage from "./pages/book";
import SetPasswordPage from "./pages/set-password";
import ManagerTrialsPage from "./pages/manager/trials";
import CandidatesPage from "./pages/manager/candidates";
import OneOnOnesPage from "./pages/manager/one-on-ones";
import ManagerStudentsPage from "./pages/manager/students";
import ParentPortal from "./pages/parent";
import ParentOnboarding from "./pages/parent/onboarding";
import { supabase } from "./supabaseClient";

function Spinner() {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
      }}
    >
      <CircularProgress />
    </Box>
  );
}

function PublicRoute() {
  const { session, loading } = useAuth();
  if (loading) return null;
  if (session) return <Navigate to="/" replace />;
  return <LoginPage />;
}

function StudentGate({
  children,
  requireFullAccess = false,
}: {
  children: React.ReactNode;
  /** When true, trial students are bounced to the dashboard with a notice. */
  requireFullAccess?: boolean;
}) {
  const { session, studentLifecycle, loading } = useAuth();
  const [checked, setChecked] = useState(false);
  const [hasProfile, setHasProfile] = useState(false);

  useEffect(() => {
    async function check() {
      if (!session) {
        setChecked(true);
        return;
      }
      const { data } = await supabase
        .from("student_profile")
        .select("student_id")
        .eq("user_id", session.user.id)
        .maybeSingle();
      setHasProfile(!!data);
      setChecked(true);
    }
    check();
  }, [session]);

  if (loading || !checked) return <Spinner />;
  if (!hasProfile) return <Onboarding onComplete={() => setHasProfile(true)} />;

  // Lifecycle gates — for student users, lifecycle is the source of truth.
  if (studentLifecycle) {
    if (studentLifecycle.lifecycle_state === "withdrawn") {
      return (
        <Box sx={{ p: 6, textAlign: "center", maxWidth: 480, mx: "auto" }}>
          <h2>Account closed</h2>
          <p>
            Your account has been withdrawn. If you'd like to re-enroll, please
            contact your office.
          </p>
        </Box>
      );
    }
    if (requireFullAccess) {
      const state = studentLifecycle.lifecycle_state;
      if (state === "trial_active" || state === "trial_expired") {
        return <Navigate to="/?locked=trial" replace />;
      }
    }
  }

  return <>{children}</>;
}

// Parent home: link a student on first login, then follow their progress and
// manage email notifications. Rendered inside AppShell (same chrome as students).
function ParentHome() {
  const { session } = useAuth();
  const [hasLink, setHasLink] = useState<boolean | null>(null);

  const loadLinks = React.useCallback(async () => {
    if (!session) return;
    const { data } = await supabase
      .from("parent_student_link")
      .select("student_id")
      .eq("parent_user_id", session.user.id)
      .limit(1);
    setHasLink(!!data?.length);
  }, [session]);

  useEffect(() => {
    loadLinks();
  }, [loadLinks]);

  if (hasLink === null)
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Spinner />
      </Box>
    );
  return hasLink ? (
    <ParentPortal />
  ) : (
    <ParentOnboarding onComplete={loadLinks} />
  );
}

function AppRoutes() {
  const { session, role, loading } = useAuth();

  if (loading) return <Spinner />;
  if (!session) return <Navigate to="/login" replace />;
  // Wait for profile fetch to populate role before rendering routes whose
  // gating depends on it. Without this, fresh-tab opens of staff-only
  // routes briefly see role=null and bounce to "/" via the route's
  // Navigate fallback (the URL replace happens before role arrives).
  if (!role) return <Spinner />;

  // ── Parents ────────────────────────────────────────────────────────────────
  // Same shell as students (Dashboard / My Profile / Exit); Dashboard is the
  // portal (link student, follow progress, manage notifications).
  if (role === "parent") {
    return (
      <AppShell>
        <Routes>
          <Route path="/" element={<ParentHome />} />
          <Route path="/profile" element={<ProfilePage />} />
          {/* Their own child's work, as the status-report email links to it.
              Same page the mentor and student read, with three differences
              ViewAsContext and the page itself enforce: approved sections
              only, "Started" / "To be started" for the rest, and no AI
              abstract — the email already gave them that, and one claim in
              two places is how the two drift apart.

              Must sit ABOVE the catch-all below, which would otherwise send
              every emailed link back to the portal home. ViewAsContext checks
              the parent_student_link before resolving the student, and RLS
              refuses the rows regardless, so this is not the only guard. */}
          <Route
            path="/view-as/:studentId/summary"
            element={<ViewAsSummaryPage />}
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AppShell>
    );
  }

  // ── Single shell for student + all staff roles ─────────────────────────────
  function homePage() {
    switch (role) {
      case "admin":
      case "instructor":
      case "office_manager":
        return <AdminDashboard />;
      case "mentor":
        return <MentorDashboard />;
      case "student":
        return (
          <StudentGate>
            <StudentDashboard />
          </StudentGate>
        );
      default:
        return (
          <Box sx={{ p: 4 }}>
            Role <strong>{role}</strong> has no dashboard yet.
          </Box>
        );
    }
  }

  const isStaff =
    role === "admin" ||
    role === "office_manager" ||
    role === "instructor" ||
    role === "mentor";

  return (
    <AppShell>
      <Routes>
        <Route path="/" element={homePage()} />

        {/* Lifecycle pages — admin + office_manager (instructors/mentors get scoped views in PR 8) */}
        <Route
          path="/candidates"
          element={
            role === "admin" || role === "office_manager" ? (
              <CandidatesPage />
            ) : (
              <Navigate to="/" replace />
            )
          }
        />
        <Route
          path="/one-on-ones"
          element={
            role === "admin" ||
            role === "office_manager" ||
            role === "mentor" ? (
              <OneOnOnesPage />
            ) : (
              <Navigate to="/" replace />
            )
          }
        />
        <Route
          path="/trials"
          element={
            role === "admin" || role === "office_manager" ? (
              <ManagerTrialsPage />
            ) : (
              <Navigate to="/" replace />
            )
          }
        />
        <Route
          path="/students"
          element={
            isStaff ? <ManagerStudentsPage /> : <Navigate to="/" replace />
          }
        />

        {/* Mentors moved into /staff in PR 6; redirect any old links */}
        <Route path="/mentors" element={<Navigate to="/staff" replace />} />
        <Route
          path="/manager/mentors"
          element={<Navigate to="/staff" replace />}
        />

        {/* Classes — admin, office_manager, instructor */}
        <Route
          path="/classes"
          element={
            role === "admin" ||
            role === "office_manager" ||
            role === "instructor" ||
            role === "mentor" ? (
              <ClassesPage />
            ) : (
              <Navigate to="/" replace />
            )
          }
        />
        <Route
          path="/classes/:classId"
          element={
            role === "admin" ||
            role === "office_manager" ||
            role === "instructor" ||
            role === "mentor" ? (
              <ClassDetailPage />
            ) : (
              <Navigate to="/" replace />
            )
          }
        />

        {/* AI-assisted outbound email — all staff */}
        <Route
          path="/compose"
          element={
            role === "admin" ||
            role === "office_manager" ||
            role === "instructor" ||
            role === "mentor" ? (
              <ComposePage />
            ) : (
              <Navigate to="/" replace />
            )
          }
        />

        {/* Fair important-dates manager — office managers do most of this; admins too */}
        <Route
          path="/fair-dates"
          element={
            role === "admin" || role === "office_manager" ? (
              <FairDatesPage />
            ) : (
              <Navigate to="/" replace />
            )
          }
        />

        {/* Staff management — admin + office_manager */}
        <Route
          path="/staff"
          element={
            role === "admin" || role === "office_manager" ? (
              <StaffPage />
            ) : (
              <Navigate to="/" replace />
            )
          }
        />
        <Route path="/users" element={<Navigate to="/staff" replace />} />

        {/* System (admin only) */}
        <Route
          path="/admin/science-fairs"
          element={
            role === "admin" ? (
              <ScienceFairsAdminPage />
            ) : (
              <Navigate to="/" replace />
            )
          }
        />
        <Route
          path="/admin/token-usage"
          element={
            role === "admin" ? <TokenUsagePage /> : <Navigate to="/" replace />
          }
        />

        {/* Student step pages */}
        <Route
          path="/step/1"
          element={
            <StudentGate>
              <Step1Page />
            </StudentGate>
          }
        />
        {/* Students go through the profile/lifecycle gate; staff get standalone
            self-study access (no student_profile) so they can answer student questions. */}
        <Route
          path="/learn/ai-research-lab"
          element={
            isStaff ? (
              <AiResearchLabPage />
            ) : (
              <StudentGate>
                <AiResearchLabPage />
              </StudentGate>
            )
          }
        />
        {/* Finished boards from past students — what Step 8 is aiming at.
            Same gate as the Lab: staff read it standalone to answer questions,
            students come through the profile/lifecycle gate. */}
        <Route
          path="/learn/boards"
          element={
            isStaff ? (
              <ExampleBoardsPage />
            ) : (
              <StudentGate>
                <ExampleBoardsPage />
              </StudentGate>
            )
          }
        />
        <Route
          path="/step/2"
          element={
            <StudentGate requireFullAccess>
              <Step2Page />
            </StudentGate>
          }
        />
        <Route
          path="/step/3"
          element={
            <StudentGate requireFullAccess>
              <Step3Page />
            </StudentGate>
          }
        />
        <Route
          path="/step/4"
          element={
            <StudentGate requireFullAccess>
              <Step4Page />
            </StudentGate>
          }
        />
        <Route
          path="/step/5"
          element={
            <StudentGate requireFullAccess>
              <Step5Page />
            </StudentGate>
          }
        />
        <Route
          path="/step/6"
          element={
            <StudentGate requireFullAccess>
              <Step6Page />
            </StudentGate>
          }
        />
        <Route
          path="/step/:stepNumber"
          element={
            <StudentGate requireFullAccess>
              <StepPage />
            </StudentGate>
          }
        />

        <Route path="/dashboard/mentor" element={<MentorDashboard />} />
        <Route path="/profile" element={<ProfilePage />} />
        <Route
          path="/my-class"
          element={
            <StudentGate>
              <MyClassPage />
            </StudentGate>
          }
        />
        <Route
          path="/messages"
          element={
            <StudentGate>
              <StudentMessagesPage />
            </StudentGate>
          }
        />
        <Route
          path="/requirements"
          element={
            <StudentGate requireFullAccess>
              <RequirementsPage />
            </StudentGate>
          }
        />
        {/* The student's own work read as a paper. Same page the mentor
            summary uses; the AI abstract is withheld from the student. */}
        <Route
          path="/summary"
          element={
            <StudentGate>
              <ViewAsSummaryPage />
            </StudentGate>
          }
        />
        <Route
          path="/progress"
          element={
            role === "admin" ||
            role === "office_manager" ||
            role === "instructor" ||
            role === "mentor" ? (
              <ProgressGridPage />
            ) : (
              <Navigate to="/" replace />
            )
          }
        />
        <Route
          path="/schedule"
          element={
            role === "admin" ||
            role === "office_manager" ||
            role === "instructor" ||
            role === "mentor" ? (
              <SchedulePage />
            ) : (
              <Navigate to="/" replace />
            )
          }
        />
        <Route
          path="/review/:projectId"
          element={
            role === "admin" ||
            role === "office_manager" ||
            role === "instructor" ||
            role === "mentor" ? (
              <MentorReviewPage />
            ) : (
              <Navigate to="/" replace />
            )
          }
        />

        {/* View-as-student mirror — staff only, read-only views of student portal */}
        <Route
          path="/view-as/:studentId"
          element={isStaff ? <Outlet /> : <Navigate to="/" replace />}
        >
          <Route index element={<Navigate to="dashboard" replace />} />
          <Route path="dashboard" element={<StudentDashboard />} />
          {/* The work as a paper, for a mentor. Staff-only by the parent route. */}
          <Route path="summary" element={<ViewAsSummaryPage />} />
          {/* The whole exchange with this student, both directions. */}
          <Route path="messages" element={<ViewAsMessagesPage />} />
          <Route path="step/1" element={<Step1Page />} />
          <Route path="learn/ai-research-lab" element={<AiResearchLabPage />} />
          <Route path="learn/boards" element={<ExampleBoardsPage />} />
          <Route path="step/2" element={<Step2Page />} />
          <Route path="step/3" element={<Step3Page />} />
          <Route path="step/4" element={<Step4Page />} />
          <Route path="step/5" element={<Step5Page />} />
          <Route path="step/6" element={<Step6Page />} />
          <Route path="step/:stepNumber" element={<StepPage />} />
          <Route path="my-class" element={<MyClassPage />} />
          <Route path="profile" element={<ProfilePage />} />
        </Route>

        {/* Legacy redirects to canonical paths */}
        <Route
          path="/manager/trials"
          element={<Navigate to="/trials" replace />}
        />
        <Route
          path="/manager/students"
          element={<Navigate to="/students" replace />}
        />
        <Route
          path="/manager/mentors"
          element={<Navigate to="/mentors" replace />}
        />
        <Route
          path="/manager/leads"
          element={<Navigate to="/trials" replace />}
        />
        <Route
          path="/manager/users"
          element={<Navigate to="/students" replace />}
        />
        <Route
          path="/manager/enroll"
          element={<Navigate to="/students" replace />}
        />
        <Route
          path="/admin/trials"
          element={<Navigate to="/trials" replace />}
        />
        <Route
          path="/admin/students"
          element={<Navigate to="/students" replace />}
        />
        <Route
          path="/admin/leads"
          element={<Navigate to="/trials" replace />}
        />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AppShell>
  );
}

// Shown when an invite / set-password link has expired. Supabase redirects back
// with an `error` hash (e.g. error_code=otp_expired) instead of a usable token,
// so without this the user silently lands on /login with no way to continue.
function ExpiredLinkPage() {
  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 3,
        bgcolor: "background.default",
      }}
    >
      <Box sx={{ maxWidth: 460, textAlign: "center" }}>
        <Typography variant="h5" sx={{ fontWeight: 800, mb: 1.5 }}>
          Your setup link has expired
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 3, lineHeight: 1.65 }}>
          For security, account setup links are only valid for a short time.
          Please ask the person who invited you to send a fresh link, or email
          us at{" "}
          <a href="mailto:care@ardentacademy.com" style={{ color: "#C8973A" }}>
            care@ardentacademy.com
          </a>{" "}
          and we'll get you a new one right away.
        </Typography>
        <Button
          variant="contained"
          onClick={() => {
            window.location.hash = "";
            window.location.assign("/login");
          }}
        >
          Go to sign in
        </Button>
      </Box>
    </Box>
  );
}

function InviteGate({ children }: { children: React.ReactNode }) {
  const hash = window.location.hash;
  // Parent invite: /parent-setup?token=… carries our own single-use token
  // (non-expiring), replacing Supabase's short-lived recovery links.
  // /account-setup is the same one-click sign-in for a student starting a free
  // trial (activate-trial emails it); the token works for any user.
  const inviteToken =
    window.location.pathname === "/parent-setup" ||
    window.location.pathname === "/account-setup"
      ? new URLSearchParams(window.location.search).get("token")
      : null;
  const [signedIn, setSignedIn] = useState(false);
  const [needsPassword, setNeedsPassword] = useState(
    hash.includes("type=invite") || hash.includes("type=recovery"),
  );
  /* A family booking a 1-on-1 has no account, so /book renders here, ahead of
     every session check — the same reason /parent-setup is handled here. It
     sits AFTER the hooks above: an early return before a useState breaks the
     rule that hooks run in the same order on every render. */
  if (window.location.pathname === "/book") return <BookMeetingPage />;
  /* "Start my free trial" from the thank-you email — pressed by a student or a
     parent, neither signed in. */
  if (window.location.pathname === "/trial") return <TrialAccessPage />;
  /* "Keep it open" / "Close my application" from the quiet-application email —
     same story: pressed by a student or a parent, neither signed in. */
  if (window.location.pathname === "/application")
    return <ApplicationChoicePage />;
  // An expired/invalid link comes back with an error hash and no usable token.
  const linkExpired =
    !needsPassword &&
    hash.includes("error") &&
    (hash.includes("otp_expired") ||
      hash.includes("access_denied") ||
      hash.includes("error_code"));

  /* An invited parent is signed in by the link itself; no password is asked
     for. SetPasswordPage still serves Supabase invite/recovery hashes. */
  if (inviteToken && !signedIn) {
    return (
      <ParentSignInPage
        token={inviteToken}
        onComplete={() => setSignedIn(true)}
      />
    );
  }
  if (needsPassword) {
    return <SetPasswordPage onComplete={() => setNeedsPassword(false)} />;
  }
  if (linkExpired) {
    return <ExpiredLinkPage />;
  }
  return <>{children}</>;
}

/** Users who signed in with a staff-set password used to be stopped here and
 *  made to choose a new one before they could use anything. These are not
 *  high-security accounts — a student's science-fair notes — and the screen
 *  cost more than it bought: the one student who met it had a working password
 *  and a first session the next day, and the wall was the first thing the
 *  product showed her.
 *
 *  Setting a password is still offered, and still required where it actually
 *  matters: an emailed invite or recovery link lands on SetPasswordPage above,
 *  because those links carry no password of the user's own. */
function MustChangePasswordGate({ children }: { children: React.ReactNode }) {
  return <>{children}</>;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <ViewAsProvider>
          <InviteGate>
            <MustChangePasswordGate>
              <Routes>
                <Route path="/login" element={<PublicRoute />} />
                <Route path="/*" element={<AppRoutes />} />
              </Routes>
            </MustChangePasswordGate>
          </InviteGate>
        </ViewAsProvider>
      </BrowserRouter>
    </AuthProvider>
  );
}
